import { requestApiJson } from './client';

export type EventMarketingPhase = 'draft' | 'planning' | 'active' | 'review' | 'complete';
export type EventMarketingInitiativeStatus = 'planned' | 'active' | 'done' | 'cancelled';
export type EventMarketingInitiativeScheduleKind = 'period' | 'single';
export type EventMarketingMetricStage = 'output' | 'outtake' | 'outcome' | 'impact';

export type EventMarketingPrimaryKpi = {
  name: string;
  baseline: number | null;
  target: number | null;
  unit: string;
};

export type EventMarketingInitiative = {
  id?: string;
  title: string;
  channel: string;
  status: EventMarketingInitiativeStatus;
  scheduleKind: EventMarketingInitiativeScheduleKind;
  scheduledAt?: string;
  startOn?: string;
  endOn?: string;
  ownerAdminId?: number;
  plannedBudget?: number;
  actualBudget?: number;
  message?: string;
  notes?: string;
};

export type EventMarketingLog = {
  id?: string;
  initiativeId?: string;
  occurredAt: string;
  summary: string;
  spend?: number;
  evidenceUrl?: string;
  resultNote?: string;
};

export type EventMarketingMetric = {
  id?: string;
  initiativeId?: string;
  name: string;
  stage: EventMarketingMetricStage;
  value: number;
  unit?: string;
  observedOn: string;
  source?: string;
  note?: string;
};

export type EventMarketingLearning = {
  id?: string;
  summary: string;
  evidence?: string;
  conditions?: string;
  nextAction?: string;
  reusable: boolean;
};

export type EventMarketing = {
  version: number;
  phase: EventMarketingPhase;
  objective: string;
  audience: string;
  audienceInsight: string;
  keyMessage: string;
  primaryKpi: EventMarketingPrimaryKpi;
  plannedBudget: number | null;
  notes: string;
  initiatives: EventMarketingInitiative[];
  logs: EventMarketingLog[];
  metrics: EventMarketingMetric[];
  learnings: EventMarketingLearning[];
};

export type EventMarketingContext = {
  eventUuid: string;
  name: string;
  eventDate: string | null;
};

export type EventMarketingResponse = {
  success: true;
  event: EventMarketingContext;
  marketing: EventMarketing;
};

export type EventMarketingValidationError = { path: string; message: string };

export class EventMarketingApiError extends Error {
  status: number;
  code: string;
  validationErrors: EventMarketingValidationError[];
  latestMarketing: EventMarketing | null;

  constructor(
    message: string,
    options: {
      status: number;
      code?: string;
      validationErrors?: EventMarketingValidationError[];
      latestMarketing?: EventMarketing | null;
    },
  ) {
    super(message);
    this.name = 'EventMarketingApiError';
    this.status = options.status;
    this.code = options.code || '';
    this.validationErrors = options.validationErrors || [];
    this.latestMarketing = options.latestMarketing || null;
  }
}

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const stringValue = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const optionalString = (value: unknown): string | undefined => {
  const normalized = stringValue(value);
  return normalized || undefined;
};

const finiteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};

const optionalNumber = (value: unknown): number | undefined => {
  const normalized = finiteNumber(value);
  return normalized === null ? undefined : normalized;
};

const oneOf = <T extends string>(value: unknown, values: readonly T[], fallback: T): T => {
  const normalized = stringValue(value) as T;
  return values.includes(normalized) ? normalized : fallback;
};

export const createEmptyEventMarketing = (): EventMarketing => ({
  version: 0,
  phase: 'draft',
  objective: '',
  audience: '',
  audienceInsight: '',
  keyMessage: '',
  primaryKpi: { name: '', baseline: null, target: null, unit: '' },
  plannedBudget: null,
  notes: '',
  initiatives: [],
  logs: [],
  metrics: [],
  learnings: [],
});

const normalizeInitiative = (value: unknown): EventMarketingInitiative => {
  const source = record(value);
  return {
    ...(optionalString(source.id) ? { id: optionalString(source.id) } : {}),
    title: stringValue(source.title),
    channel: stringValue(source.channel),
    status: oneOf(source.status, ['planned', 'active', 'done', 'cancelled'] as const, 'planned'),
    scheduleKind: oneOf(source.scheduleKind, ['period', 'single'] as const, 'period'),
    ...(optionalString(source.scheduledAt) ? { scheduledAt: optionalString(source.scheduledAt) } : {}),
    ...(optionalString(source.startOn) ? { startOn: optionalString(source.startOn) } : {}),
    ...(optionalString(source.endOn) ? { endOn: optionalString(source.endOn) } : {}),
    ...(optionalNumber(source.ownerAdminId) !== undefined
      ? { ownerAdminId: optionalNumber(source.ownerAdminId) }
      : {}),
    ...(optionalNumber(source.plannedBudget) !== undefined
      ? { plannedBudget: optionalNumber(source.plannedBudget) }
      : {}),
    ...(optionalNumber(source.actualBudget) !== undefined
      ? { actualBudget: optionalNumber(source.actualBudget) }
      : {}),
    ...(optionalString(source.message) ? { message: optionalString(source.message) } : {}),
    ...(optionalString(source.notes) ? { notes: optionalString(source.notes) } : {}),
  };
};

const normalizeLog = (value: unknown): EventMarketingLog => {
  const source = record(value);
  return {
    ...(optionalString(source.id) ? { id: optionalString(source.id) } : {}),
    ...(optionalString(source.initiativeId) ? { initiativeId: optionalString(source.initiativeId) } : {}),
    occurredAt: stringValue(source.occurredAt),
    summary: stringValue(source.summary),
    ...(optionalNumber(source.spend) !== undefined ? { spend: optionalNumber(source.spend) } : {}),
    ...(optionalString(source.evidenceUrl) ? { evidenceUrl: optionalString(source.evidenceUrl) } : {}),
    ...(optionalString(source.resultNote) ? { resultNote: optionalString(source.resultNote) } : {}),
  };
};

const normalizeMetric = (value: unknown): EventMarketingMetric => {
  const source = record(value);
  return {
    ...(optionalString(source.id) ? { id: optionalString(source.id) } : {}),
    ...(optionalString(source.initiativeId) ? { initiativeId: optionalString(source.initiativeId) } : {}),
    name: stringValue(source.name),
    stage: oneOf(source.stage, ['output', 'outtake', 'outcome', 'impact'] as const, 'output'),
    value: finiteNumber(source.value) ?? 0,
    ...(optionalString(source.unit) ? { unit: optionalString(source.unit) } : {}),
    observedOn: stringValue(source.observedOn),
    ...(optionalString(source.source) ? { source: optionalString(source.source) } : {}),
    ...(optionalString(source.note) ? { note: optionalString(source.note) } : {}),
  };
};

const normalizeLearning = (value: unknown): EventMarketingLearning => {
  const source = record(value);
  return {
    ...(optionalString(source.id) ? { id: optionalString(source.id) } : {}),
    summary: stringValue(source.summary),
    ...(optionalString(source.evidence) ? { evidence: optionalString(source.evidence) } : {}),
    ...(optionalString(source.conditions) ? { conditions: optionalString(source.conditions) } : {}),
    ...(optionalString(source.nextAction) ? { nextAction: optionalString(source.nextAction) } : {}),
    reusable: source.reusable === true,
  };
};

export const normalizeEventMarketing = (value: unknown): EventMarketing => {
  const source = record(value);
  const kpi = record(source.primaryKpi);
  return {
    version: Math.max(0, Math.floor(finiteNumber(source.version) ?? 0)),
    phase: oneOf(source.phase, ['draft', 'planning', 'active', 'review', 'complete'] as const, 'draft'),
    objective: stringValue(source.objective),
    audience: stringValue(source.audience),
    audienceInsight: stringValue(source.audienceInsight),
    keyMessage: stringValue(source.keyMessage),
    primaryKpi: {
      name: stringValue(kpi.name),
      baseline: finiteNumber(kpi.baseline),
      target: finiteNumber(kpi.target),
      unit: stringValue(kpi.unit),
    },
    plannedBudget: finiteNumber(source.plannedBudget),
    notes: stringValue(source.notes),
    initiatives: Array.isArray(source.initiatives) ? source.initiatives.map(normalizeInitiative) : [],
    logs: Array.isArray(source.logs) ? source.logs.map(normalizeLog) : [],
    metrics: Array.isArray(source.metrics) ? source.metrics.map(normalizeMetric) : [],
    learnings: Array.isArray(source.learnings) ? source.learnings.map(normalizeLearning) : [],
  };
};

export const normalizeEventMarketingResponse = (value: unknown): EventMarketingResponse => {
  const source = record(value);
  const event = record(source.event);
  return {
    success: true,
    event: {
      eventUuid: stringValue(event.eventUuid),
      name: stringValue(event.name),
      eventDate: event.eventDate == null ? null : stringValue(event.eventDate),
    },
    marketing: normalizeEventMarketing(source.marketing),
  };
};

const throwResponseError = (status: number, value: unknown, fallback: string): never => {
  const source = record(value);
  const error = record(source.error);
  const rawErrors = Array.isArray(error.fields)
    ? error.fields
    : Array.isArray(source.errors)
      ? source.errors
      : [];
  const validationErrors = rawErrors
    .map((item) => record(item))
    .map((item) => ({ path: stringValue(item.path), message: stringValue(item.message) }))
    .filter((item) => item.path || item.message);
  throw new EventMarketingApiError(stringValue(source.message) || fallback, {
    status,
    code: stringValue(source.code) || stringValue(error.code),
    validationErrors,
    latestMarketing: source.marketing ? normalizeEventMarketing(source.marketing) : null,
  });
};

export async function getEventMarketing(
  eventUuid: string,
  options: { signal?: AbortSignal } = {},
): Promise<EventMarketingResponse> {
  const result = await requestApiJson(`/admin/events/${encodeURIComponent(eventUuid)}/marketing`, {
    method: 'GET',
    signal: options.signal,
  });
  if (!result.response.ok) {
    throwResponseError(result.response.status, result.error || result.data, 'マーケティング情報を取得できませんでした。');
  }
  return normalizeEventMarketingResponse(result.data);
}

export async function updateEventMarketing(
  eventUuid: string,
  marketing: EventMarketing,
): Promise<EventMarketingResponse> {
  const result = await requestApiJson(`/admin/events/${encodeURIComponent(eventUuid)}/marketing`, {
    method: 'PUT',
    body: normalizeEventMarketing(marketing),
  });
  if (!result.response.ok) {
    throwResponseError(result.response.status, result.error || result.data, 'マーケティング情報を保存できませんでした。');
  }
  return normalizeEventMarketingResponse(result.data);
}
